import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useParams, Prompt } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { token } from '@atlaskit/tokens';
import styled from '@emotion/styled';
import classNames from 'classnames';
import Button, { ButtonGroup } from '@atlaskit/button';
import { CCP_SEARCH_MAX_LIMIT, STATSIG_FEATURE_GATES, routePaths } from '@css-xp/ppc-constants';
import { fetchShipToInPPC } from '@css-xp/ppc-sold-to-address-table';
import EmptyState from '@atlaskit/empty-state';
import { StyledVerticalDivider } from './ui/billing-profile/src/components/billing-profile-card.styles';
import Spinner from '@atlaskit/spinner';
import EditIcon from '@atlaskit/icon/glyph/edit';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Text, Flex, Inline, Stack } from '@atlaskit/primitives';
import { Y300, N30A, N800 } from '@atlaskit/theme/colors';
import { getRouteUrl, splitAndCapitalize, useQueryParams } from '@css-xp/ppc-utils';
import Tooltip from '@atlaskit/tooltip';
import Drawer from '@atlaskit/drawer';
import SendIcon from '@atlaskit/icon/glyph/send';
import ArrowLeftCircleIcon from '@atlaskit/icon/glyph/arrow-left-circle';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import WatchIcon from '@atlaskit/icon/glyph/watch';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import {
  AnalyticsEventSource,
  AnalyticsTrackAction,
  AnalyticsTrackSubject,
  sendAnalyticsEvent,
  trackButtonClick,
} from '@atlassian/temp-analytics';
import Checkbox from '@atlaskit/checkbox';
import { SimpleTag as Tag } from '@atlaskit/tag';
import {
  AppContent,
  AppPage,
  CollapsiblePricingEstimate,
  getFormattedCurrency,
  useApplicationContext,
  useFeatureToggle,
  useModalDialog,
  DISCOUNT_LABELS,
  useNotification,
  successNotificationBuilder,
  NotificationTypes,
  errorNotificationBuilder,
} from '@css-xp/base';
import { useOpenClose, useTitle } from '@css-xp/ppc-custom-hooks';
import { InfoSection, ScreenEventAnalytics } from '@css-xp/ppc-common';

import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { PricingPlanType } from '@atlassian/cssxp-ccp-types';
import { fetchTransactionAccountForCreateBillingProfile } from '@css-xp/ppc-billing-profile-table';
import {
  FetchAvailableInvoiceGroupsForOrderReviewQuery,
  FetchAvailableInvoiceGroupsForOrderReviewQueryVariables,
  FetchTransactionAccountForCreateBillingProfileQuery,
  FetchTransactionAccountForCreateBillingProfileQueryVariables,
  FetchShipToInPpcQuery,
  FetchShipToInPpcQueryVariables,
  FetchInvoiceGroupPpcQuery,
  FetchInvoiceGroupPpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { OrderPageLoader } from '@css-xp/ppc-page-loaders';
import { PageBackgroundV2 } from '@css-xp/page-background';
import { CCPPricing } from '@atlassian/cssxp-typescommon';
import { SearchCustomerModal } from './ui/customer';
import { ProductLineItemsWrapper } from './ui/product-line-items';
import { AddNewProductsWrapper } from './ui/add-new-products';
import { BillingProfileWrapper } from './ui/billing-profile';
import { CartCustomerSearchItem } from './ui/customer/src/types';
import { OrderNowButtonWrapper, MoreActionsMenu, AddToOrderActionsMenu } from './ui/order-actions';
// @ts-ignore
import GoalLogo from './ui/customer/src/assets/goal-icon-on-track.svg';
// @ts-ignore
import EmptyCartIcon from './ui/assets/empty-cart-icon.svg';
// @ts-ignore
import EmptyCartV2Icon from './ui/assets/empty-cart-v2.svg';

import { CartFilterWrapper } from './ui/cart-filter';
import { OrderSummaryWrapper } from './ui/order-summary';
import { AddCustomerModal } from './ui/customer/src/components/add-customer-modal';
import { UpdateExistingEntitlementModal } from './ui/customer/src/components/update-existing-entitlement-modal';
import { CreateCustomerAndBillingProfileModal } from './ui/customer/src/components/create-customer-and-billing-profile-modal';
import {
  useLineItemsRecurringEstimateStore,
  useProductLineItemsStore,
  useSelectedProductIdsWithoutChanges,
  useStore,
  useIsLatestBillEstimateFetched,
  useHasProductEnrichmentFailed,
  useIsProductEnrichmentPending,
  useDestinationBillingProfileDetails,
  useOrgProductInstancesMap,
} from './store';
import { CurrentScreenTypeEnum } from './store/types';
import { useEstimateDownload } from './hooks/use-estimate-download';
import { fetchInvoiceGroupDetails } from './queries/fetch-invoice-group-details';
import { fetchAvailableInvoiceGroupsForOrderReview } from './ui/order-summary/src/queries/fetch-available-invoice-groups';
import { EstimateModalWrapper } from './ui/bill-estimation';
import { EstimateAndOrderActionMenu } from './ui/order-actions/src/estimate-and-order-action-menu';
import { ProductTypeEnum } from './ui/product-line-items/src/types';
import { DestinationBillingProfileWrapper } from './ui/billing-profile/src/components/destination-billing-profile-wrapper';
import { CreateBillingProfileModalForOrderFlow } from './ui/customer/src/components/create-billing-profile-modal';
import { getCurrentScreenPageTitle, transformBillingProfileDetails } from './utils';
import { BillingProfileResponse } from './types';
import { TransferEntitlementsModal } from './ui/entitlement-transfer/src/components/transfer-entitlements-modal';
import { AddNewProductsModal } from './ui/add-new-products/src/components/add-new-products-modal';
import { OrderConfirmationContainer } from './ui/order-summary/src/components/order-confirmation-container /order-confirmation-container';
import './order-page.scss';
import { SelectType } from './ui/billing-profile/src/types';
import { NewAppPurchaseModal } from './ui/product-line-items/src/components/new-app-purchase-modal';
import { CatalogueContextItems } from './ui/add-new-products/src/types';
import { useBillingProfileAndEntitlements } from './ui/billing-profile/src/hooks/use-billing-profile-and-entitlements';
import { IncludedProductsSidebar } from './ui/product-line-items/src/components/included-products-sidebar';
import { CART_ROUTE_PARAMS } from './constants';

type drawerBackButtonProp = {
  setSummaryDrawerOpen: (isOpen: boolean) => void;
  currentScreen?: CurrentScreenTypeEnum;
  resetStore: () => void;
  setSelectedCustomer: (customer: CartCustomerSearchItem) => void;
  setCustomer: (customer: CartCustomerSearchItem) => void;
  open: () => void;
  isDesignIteration1Enabled?: boolean;
};

export type OrderDetailsProps = {
  hasOrderBeenPlaced: boolean;
  newSiteOrderId: string;
  omsResponse: any;
};

const StyledEstimateInfoSection = styled.div`
  > div > div > div {
    > div {
      > span:nth-child(2) {
        font-size: ${token('space.250')} !important;
      }
    }
    &:nth-child(6) > div > span:nth-child(2) {
      font-weight: ${token('font.weight.semibold')};
    }
    &:nth-child(5) > div {
      margin-left: ${token('space.250')};
      margin-right: ${token('space.500')};
      border-right: 1px solid ${token('color.border.input')};
    }
  }
`;

const StyledOrderPageBackground = styled.div`
  .page-header-container {
    > div {
      > div:nth-child(2) {
        > div > h1 {
          margin-top: ${token('space.100')};
        }
      }
    }
  }
`;

const OrderMainContainer = styled.div(() => ({
  position: 'inherit',
  zIndex: 0,
}));

const StyledEmptySelectedCustomer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 60vh;
  > div:nth-child(1) {
    margin-bottom: ${token('space.250')};
  }
`;

const ESTIMATE_PRICING_ROW_LABELS = {
  TOTAL_MONTHLY_RECURRING_ESTIMATE: 'TOTAL MONTHLY RECURRING ESTIMATE',
  MONTHLY_RECURRING_ESTIMATE: 'MONTHLY RECURRING ESTIMATE',
  TOTAL_ITEM_SUBTOTAL: 'TOTAL ITEM SUBTOTAL',
  TOTAL_PARTNER_DISCOUNT: 'TOTAL PARTNER DISCOUNT',
  TOTAL_OTHER_DISCOUNT: 'TOTAL OTHER DISCOUNT',
  ADMIN_FEE: 'ADMIN FEE',
  TOTAL_TAX: 'TOTAL TAX',
};

const LineItemsContainer = ({ children }: { children: JSX.Element }) => {
  return children;
};

const OrderHeader = ({ children }: { children: JSX.Element }) => {
  return children;
};

export enum ORDER_PAGE_SIDEBAR {
  INCLUDED_PRODUCTS = 'included-products',
}

export const allowedSidebars = {
  [ORDER_PAGE_SIDEBAR.INCLUDED_PRODUCTS]: IncludedProductsSidebar,
};

const RightSideBar = styled.div((props: { isActiveEntitlementsOpen: boolean }) => ({
  display: props.isActiveEntitlementsOpen ? 'block' : 'none',
  zIndex: 1,
}));

const EmptyCart = ({
  isActiveEntitlementsOpen,
  setActiveEntitlementsOpen,
  hasNewCloudSiteProducts,
}: {
  isActiveEntitlementsOpen: boolean;
  setActiveEntitlementsOpen: (flag: boolean) => void;
  hasNewCloudSiteProducts: boolean;
}) => {
  return (
    <div className="flex flex-column align-center product-line-items-wrapper--empty-order">
      {/* @ts-ignore */}
      <img src={EmptyCartIcon} alt="No items in cart" />
      <h1 className="mt-0 pt-16 pb-16">Add subscriptions</h1>
      <p className="pt-16 pb-16">Buy, change, or move them to your billing account</p>
      <div className="pt-16 pb-16">
        <ButtonGroup>
          {!isActiveEntitlementsOpen && (
            <Button
              iconBefore={<WatchIcon label="add-active" />}
              appearance="default"
              onClick={() => setActiveEntitlementsOpen(true)}
            >
              Add active
            </Button>
          )}
          <AddNewProductsWrapper cloudSiteHost={''} isDisabled={hasNewCloudSiteProducts} />
        </ButtonGroup>
      </div>
    </div>
  );
};

const EmptyCartV2 = ({
  openUpdateExistingEntitlementModal,
  onTransferClick,
  onAddNewProductsClick,
  isLoading,
  onAddNewAppOrAddOnClick,
  isAddOnsPhase2Enabled,
  isAppificationEnabled,
}: {
  isLoading?: boolean;
  openUpdateExistingEntitlementModal: () => void;
  onTransferClick: () => void;
  onAddNewProductsClick: () => void;
  onAddNewAppOrAddOnClick: () => void;
  isAddOnsPhase2Enabled?: boolean;
  isAppificationEnabled?: boolean;
}) => {
  return (
    <StyledEmptySelectedCustomer>
      <EmptyState
        header="Start by adding monthly subscriptions"
        description={
          <>
            <Text color="color.text.subtle">Purchase, update or transfer your</Text>
            <br />
            <Text color="color.text.subtle">customer's subscriptions.</Text>
          </>
        }
        imageUrl={EmptyCartV2Icon as any}
      />
      <Flex justifyContent="center" gap="space.150">
        <AddToOrderActionsMenu
          onTransferClick={onTransferClick}
          onAddNewProductsClick={onAddNewProductsClick}
          openUpdateExistingEntitlementModal={openUpdateExistingEntitlementModal}
          isLoading={isLoading}
          onAddNewAppOrAddOnClick={onAddNewAppOrAddOnClick}
          isAddOnsPhase2Enabled={isAddOnsPhase2Enabled}
          isAppificationEnabled={isAppificationEnabled}
        />
      </Flex>
    </StyledEmptySelectedCustomer>
  );
};

const CustomerName = ({
  onClick,
  customer,
  showEditCustomerButton = false,
  invoiceGroupDetails,
  showCurrencyAndPriceEligibility,
  version,
}: {
  onClick?: () => void;
  customer: CartCustomerSearchItem | null;
  showEditCustomerButton?: boolean;
  invoiceGroupDetails?: { name: string; currency: string; shipToPriceEligibility?: string };
  showCurrencyAndPriceEligibility?: boolean;
  version?: number;
}) => {
  const [isEnabled] = useFeatureToggle();
  const isVersion2 = version === 2;
  const ORDER_LABEL = 'Monthly order or estimate';

  const renderCustomerBillingProfileDetailsV2 = () => {
    return (
      <Inline space={'space.100'}>
        <Stack>
          {showCurrencyAndPriceEligibility && invoiceGroupDetails ? (
            <Flex>
              <Text color="color.text.accent.gray">{invoiceGroupDetails?.name ?? ''}</Text>
              <StyledVerticalDivider />
              <Text color="color.text.accent.gray">
                {
                  splitAndCapitalize(
                    invoiceGroupDetails?.shipToPriceEligibility ?? PricingPlanType.COMMERCIAL
                  ) as string
                }
              </Text>
              <StyledVerticalDivider />
              <Text color="color.text.accent.gray">{invoiceGroupDetails?.currency?.toUpperCase() ?? ''}</Text>
            </Flex>
          ) : null}
        </Stack>
      </Inline>
    );
  };

  return isVersion2 ? (
    renderCustomerBillingProfileDetailsV2()
  ) : (
    <div className="flex align-center">
      <Inline key={'space.100'} space={'space.100'}>
        {/* @ts-ignore */}
        <img src={GoalLogo} height="40" width="40" alt="goal icon" />
        <Stack>
          <p className="ppc-order-page-header-customer-label">{ORDER_LABEL}</p>
          <div
            id="ppc-order-page-header-customer-name"
            className="ppc-order-page-header-customer-name flex"
            data-testid="selected-customer-name"
            onClick={onClick}
          >
            {!!customer && customer.ids?.length === 0 && (
              <Tooltip content={<>Invalid customer</>} tag="span">
                <WarningIcon primaryColor={Y300} label="warning" size="medium" />
              </Tooltip>
            )}
            {customer?.name}
            {showEditCustomerButton && (
              <div className="customer-name-edit">
                <Button
                  className="customer-name-edit-button"
                  testId="customer-name-edit-button"
                  iconBefore={<EditIcon size="small" label="edit" />}
                  appearance="subtle"
                />
              </div>
            )}
          </div>
          {showCurrencyAndPriceEligibility && invoiceGroupDetails ? (
            <div>
              <Tag text={invoiceGroupDetails?.name ?? ''} />
              <Tag
                text={
                  splitAndCapitalize(
                    invoiceGroupDetails?.shipToPriceEligibility ?? PricingPlanType.COMMERCIAL
                  ) as string
                }
              />
              <Tag text={invoiceGroupDetails?.currency?.toUpperCase() ?? ''} />
            </div>
          ) : null}
        </Stack>
      </Inline>
    </div>
  );
};

const AddActiveEntitlementsSideBar = ({
  customer,
  invoiceGroupId,
  setActiveEntitlementsOpen,
  isSummaryDrawerOpen,
  isNonCommercialPricingEnabled,
}: {
  customer: CartCustomerSearchItem | null;
  invoiceGroupId: string | null;
  setActiveEntitlementsOpen: (flag: boolean) => void;
  isSummaryDrawerOpen: boolean;
  isNonCommercialPricingEnabled: boolean;
}) => {
  return (
    <div className="ppc-order-page-right-sidebar pa-24">
      <div className="flex justify-space-between align-center mb-24">
        <h2>Add active subscriptions</h2>
        <Button
          iconBefore={<CrossIcon label="close" size="small" />}
          appearance="subtle"
          onClick={() => setActiveEntitlementsOpen(false)}
        ></Button>
      </div>
      {customer && (isNonCommercialPricingEnabled ? invoiceGroupId : true) ? (
        <BillingProfileWrapper customerIds={customer.ids} isSummaryDrawerOpen={isSummaryDrawerOpen} />
      ) : null}
    </div>
  );
};
const DrawerBackButton: React.FC<drawerBackButtonProp> = ({
  setSummaryDrawerOpen,
  currentScreen,
  resetStore,
  setSelectedCustomer,
  open,
  setCustomer,
  isDesignIteration1Enabled,
}) => {
  const onClick = () => {
    setSummaryDrawerOpen(false);
    if (currentScreen === CurrentScreenTypeEnum.ORDER_CONFIRMATION) {
      resetStore();
      setCustomer({ name: '_', ids: [] });
      setSelectedCustomer({ name: '_', ids: [] });
      open();
    }
  };
  const IconBefore = useMemo(() => {
    return isDesignIteration1Enabled ? ArrowLeftIcon : ArrowLeftCircleIcon;
  }, [isDesignIteration1Enabled]);

  const content = useMemo(() => {
    return isDesignIteration1Enabled ? '' : 'Back';
  }, [isDesignIteration1Enabled]);

  return (
    <Button
      iconBefore={<IconBefore label="left-arrow" />}
      appearance="subtle"
      onClick={onClick}
      className="order-review-back-button"
    >
      {content}
    </Button>
  );
};
export const initialCustomer = { name: '_', ids: [] };
export const OrderPage = () => {
  const { txaId } = useParams<{ txaId: string }>();
  const queryParams = useQueryParams();
  const billingProfileId = queryParams.get(CART_ROUTE_PARAMS.BILLING_PROFILE) as string;
  const queryParamCustomerName = queryParams.get(CART_ROUTE_PARAMS.CUSTOMER) as string;
  const context = useApplicationContext();
  const [isEnabled] = useFeatureToggle();
  const history = useHistory();
  const initialTxaIdRef = React.useRef(txaId);
  const location = useLocation();
  const { hasCreatedNewCustomerAndBillingProfile, hasCreatedNewBillingProfile } = location.state || {};
  const isFlatDiscountSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_FLAT_DISCOUNT_SUPPORT);
  const showInactiveProducts = isEnabled(STATSIG_FEATURE_GATES.ENABLE_INACTIVE_PRODUCTS_IN_UOF);
  const isAddonEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS);
  const isJSMRepackagingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JSM_OFFERING_CHANGE);
  const isJPDPremiumOfferingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_PREMIUM_OFFERING);
  const isCompassPremiumEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_COMPASS_PREMIUM);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isSandboxDecouplingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING);
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
  const isNonCommercialPricingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING);
  const isSupportForPaypalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT);
  const isFutureDatedEntitlementEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS);
  const isCustomerContactV3Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3);
  const isJPDAsJiraFamilyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY);
  const isCoTermInUofEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CO_TERM_IN_UOF);
  const isSandboxAppCreateInUOFEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SANDBOX_APP_CREATE_IN_UOF);
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);
  const isFetchBillingProfilesViaCCPEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_FROM_CCP);
  const isTWCPhase1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);
  const isTWCPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2);
  const isExcludeExpiredPromotionsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_EXCLUDE_EXPIRED_PROMOTIONS_IN_UOF);
  const isAddOnsPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2);
  const isStatuspageEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_STATUSPAGE);
  const isAppEditionSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT);
  const isAppificationEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APPIFICATION);
  const isMultiCurrencyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
  const isLoomEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_STANDALONE_LOOM_FOR_UOF);
  const isSoldToAddressArchivalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ARCHIVE_SOLD_TO_ADDRESS);
  const isMultiInstanceAppsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS);

  const [
    {
      products,
      hasProductMetadataFetchFailed,
      isLoading,
      isRefreshEstimateLoading,
      selectedProducts,
      invoiceGroupId,
      currentScreen,
      selectedCustomer = initialCustomer,
    },
    actions,
  ] = useStore();
  const orgProductInstancesMap = useOrgProductInstancesMap();
  const { sendSuccessNotification, sendErrorNotification } = useNotification();
  const [orderDetails, setOrderDetails] = useState<OrderDetailsProps>({
    hasOrderBeenPlaced: false,
    newSiteOrderId: '',
    omsResponse: {},
  });

  /** Required for new design flow to show success notification on the cart screen */
  React.useEffect(() => {
    if (hasCreatedNewCustomerAndBillingProfile) {
      const successMessage = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('create-invoice-group-success')
        .withHeader('Success')
        .withMessage('Customer has been created successfully. You can create an order now.')
        .withDismissAfter(5000)
        .build();
      sendSuccessNotification(successMessage);
    }

    if (hasCreatedNewBillingProfile) {
      const successMessage = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('create-invoice-group-success')
        .withHeader('Success')
        .withMessage('Billing profile created successfully. You can create an order now.')
        .withDismissAfter(5000)
        .build();
      sendSuccessNotification(successMessage);
    }
  }, [hasCreatedNewCustomerAndBillingProfile, hasCreatedNewBillingProfile, sendSuccessNotification]);

  const { data, loading } = useQuery<FetchInvoiceGroupPpcQuery, FetchInvoiceGroupPpcQueryVariables>(
    fetchInvoiceGroupDetails,
    {
      variables: {
        id: billingProfileId,
        txaId,
      },
      skip: !!selectedCustomer?.ids?.length || !billingProfileId,
    }
  );

  const customerName = data?.invoiceGroup?.shipToParty?.name;

  const { data: soldToParties, loading: soldToPartiesLoading } = useQuery<
    FetchShipToInPpcQuery,
    FetchShipToInPpcQueryVariables
  >(fetchShipToInPPC, {
    variables: {
      searchParams: {
        companyName: customerName,
        transactionAccountId: txaId,
        ...(isSoldToAddressArchivalEnabled && { isActive: true }),
      },
      pageParams: {
        size: CCP_SEARCH_MAX_LIMIT,
      },
    },
    displayName: 'SoldToAddressForCompanyNameQuery',
    skip: !customerName || !!selectedCustomer?.ids?.length,
  });

  const shipToIds = soldToParties?.shipToParty?.nodes?.map((node) => node?.id);

  React.useEffect(() => {
    if (soldToParties && !loading && customerName) {
      actions.setInvoiceGroup(billingProfileId);
      actions.setSelectedCustomer({
        name: customerName,
        ids: shipToIds as string[],
      });
    }
  }, [soldToParties, loading]);

  const [newInvoiceGroupDetails, setNewInvoiceGroupDetails] = useState<{ name: string; currency: string } | undefined>(
    undefined
  );
  const [selectedProductDetails, setSelectedProductDetails] = useState<{
    product: any;
    cloudSiteId?: string;
    cloudSiteUrl?: string;
    onAddToCart: (OrderItems: CatalogueContextItems) => void;
    sandboxCloudSiteParentProductIds?: string[];
  } | null>(null);
  const [isActiveEntitlementsOpen, setActiveEntitlementsOpen] = useState<boolean>(
    isDesignIteration1Enabled ? false : true
  );
  const [isSummaryDrawerOpen, setSummaryDrawerOpen] = useState<boolean>(false);
  const [selectedCloudSiteForAddNewProducts, setSelectedCloudSiteForAddNewProducts] = useState<
    { cloudSiteId: string; cloudSiteHostName: string } | undefined
  >(undefined);
  const [isClearAllLineItems, setIsClearAllLineItems] = useState<boolean>(false);

  const [showProductListForAppSelection, setShowProductListForAppSelection] = useState<boolean>(false);
  const [hasErrorFetchingOfferings, setHasErrorFetchingOfferings] = useState<boolean>(false);

  const [productLineItems] = useProductLineItemsStore();
  const hasProductEnrichmentFailed = useHasProductEnrichmentFailed();
  const isProductEnrichmentPending = useIsProductEnrichmentPending();
  const selectedBillingProfileDetails = useDestinationBillingProfileDetails();
  const {
    isOpen: showSearchCustomerModal,
    open: openSearchCustomerModal,
    close: closeSearchCustomerModal,
  } = useOpenClose();
  const {
    isOpen: transferEntitlementsModal,
    open: openTransferEntitlementsModal,
    close: closeTransferEntitlementsModal,
  } = useOpenClose();
  const {
    isOpen: addNewProductsModal,
    open: openAddNewProductsModal,
    close: closeAddNewProductsModal,
  } = useOpenClose();
  const { isOpen: showAddCustomerModal, open: openAddCustomerModal, close: closeAddCustomerModal } = useOpenClose();
  const {
    isOpen: showDestinationBillingProfileModal,
    open: openDestinationBillingProfileModal,
    close: closeDestinationBillingProfileModal,
  } = useOpenClose();

  const {
    isOpen: showCreateCustomerAndBillingProfileModal,
    open: openCreateCustomerAndBillingProfileModal,
    close: closeCreateCustomerAndBillingProfileModal,
  } = useOpenClose();

  const {
    isOpen: showCreateBillingProfileModal,
    open: openCreateBillingProfileModal,
    close: closeCreateBillingProfileModal,
  } = useOpenClose();

  const {
    isOpen: showUpdateExistingEntitlementModal,
    open: openUpdateExistingEntitlementModal,
    close: closeUpdateExistingEntitlementModal,
  } = useOpenClose();

  const {
    isOpen: showNewAppPurchaseModal,
    open: openNewAppPurchaseModal,
    close: closeNewAppPurchaseModal,
  } = useOpenClose();

  const {
    downloadEstimatePDF,
    isEstimatePDFDownloading,
    prepareAndSaveEstimateJSON,
    isEstimateJsonIsDownloading,
  } = useEstimateDownload();
  const { closeModalDialog, isModalOpen, openModalDialog } = useModalDialog();
  const [isPartnerMarginIncluded, setIsPartnerMarginIncluded] = React.useState<boolean>(true);
  const [typeOfExportSelected, setTypeOfExport] = React.useState<'PDF' | 'JSON' | undefined>();
  const selectedCustomerName = selectedCustomer?.name;

  const {
    isBillingProfileDetailsLoading,
    billingProfileError,
    refetchBillingProfileDetails,
    fetchEntitlementListError,
    isFetchEntitlementListLoading,
    refetchEntitlementsResponse,
    externalProductsList,
    isExternalProductListLoading,
  } = useBillingProfileAndEntitlements({
    customer: selectedCustomer,
    invoiceGroupId: selectedBillingProfileDetails?.id,
    isDesignIteration1Enabled,
  });

  const selectedProductIdsWithoutChanges = useSelectedProductIdsWithoutChanges();
  const isLatestBillEstimateFetched = useIsLatestBillEstimateFetched();
  const isAnyProductLeftUnchanged = selectedProductIdsWithoutChanges.length > 0;

  const [hasUnchangedLineItems, setHasUnchangedLineItems] = useState<boolean>(isAnyProductLeftUnchanged);
  const [showUnchangedLineItems, setShowUnchangedLineItems] = useState<boolean>(false);
  const isLoggedInUserCustomerAdvocate = context?.isCustomerAdvocate;

  useEffect(() => {
    if (isSummaryDrawerOpen) {
      actions.setCurrentScreen(CurrentScreenTypeEnum.ORDER_SUMMARY);
    } else {
      actions.setCurrentScreen(CurrentScreenTypeEnum.ORDER_PAGE);
    }
  }, [isSummaryDrawerOpen]);

  const pageTitle = React.useMemo(() => getCurrentScreenPageTitle(currentScreen), [currentScreen]);

  useTitle(pageTitle);

  // reset store on Txa changes

  useEffect(() => {
    if (isDesignIteration1Enabled) {
      /**
       * For the new design, if the `txaId` is changed from the orders screen,
       * redirect the user to the select customer screen to restart the flow.
       */
      if (initialTxaIdRef.current !== txaId || !queryParamCustomerName || !billingProfileId) {
        history.push(getRouteUrl(routePaths.cart, { txaId }));
      }
    } else {
      actions.resetStore();
      actions.setSelectedCustomer(initialCustomer);
      openSearchCustomerModal();
    }
    return () => {
      actions.resetStore();
      actions.setSelectedCustomer(initialCustomer);
    };
  }, [actions, txaId, openSearchCustomerModal]);

  useEffect(() => {
    if (hasErrorFetchingOfferings) {
      const notificationOptions = errorNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withHeader(`Error`)
        .withMessage(
          `We couldn't fetch offerings for some of the linked entitlements. Please try adding to the cart again.`
        )
        .withDismissAfter(5000)
        .build();
      sendErrorNotification(notificationOptions);
    }
  }, [hasErrorFetchingOfferings]);

  // get native products with offering and pricing plan when cart page reloads
  useEffect(() => {
    actions.getNativeProductsWithOfferingsAndPricingPlans({
      filterOptions: [
        {
          key: 'includeProperties',
          values: ['vendorName'],
        },
      ],
      includeExperimentalProducts: isEnabled(STATSIG_FEATURE_GATES.ENABLE_EXPERIMENTAL_PRODUCTS_IN_CATALOG),
      isMultiCurrencyEnabled,
      isAddOnsSearchEnabled: isAddOnsPhase2Enabled,
      isStatuspageEnabled,
      isTeamWorkCollectionEnabled: isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2),
      isStrategyCollectionEnabled: isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions]);

  // place all flags in store directly from here
  useEffect(() => {
    actions.setFeatureFlag({
      [STATSIG_FEATURE_GATES.ENABLE_FLAT_DISCOUNT_SUPPORT]: !!isFlatDiscountSupportEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_INACTIVE_PRODUCTS_IN_UOF]: !!showInactiveProducts,
      [STATSIG_FEATURE_GATES.ENABLE_ADD_ONS]: !!isAddonEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_JSM_OFFERING_CHANGE]: !!isJSMRepackagingEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_COMPASS_PREMIUM]: !!isCompassPremiumEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS]: !!isSandboxSupportForSubscriptionsEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_JPD_PREMIUM_OFFERING]: !!isJPDPremiumOfferingEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING]: !!isSandboxDecouplingEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES]: !!isMultiCurrencyChangesEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING]: !!isNonCommercialPricingEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT]: !!isSupportForPaypalEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS]: !!isFutureDatedEntitlementEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1]: !!isTWCPhase1Enabled,
      [STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2]: !!isTWCPhase2Enabled,
      [STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY]: !!isJPDAsJiraFamilyEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_CO_TERM_IN_UOF]: !!isCoTermInUofEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1]: !!isDesignIteration1Enabled,
      [STATSIG_FEATURE_GATES.ENABLE_SANDBOX_APP_CREATE_IN_UOF]: !!isSandboxAppCreateInUOFEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_FROM_CCP]: !!isFetchBillingProfilesViaCCPEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_EXCLUDE_EXPIRED_PROMOTIONS_IN_UOF]: !!isExcludeExpiredPromotionsEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2]: !!isAddOnsPhase2Enabled,
      [STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT]: !!isAppEditionSupportEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES]: !!isMultiCurrencyEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_STATUSPAGE]: !!isStatuspageEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_APPIFICATION]: !!isAppificationEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_STANDALONE_LOOM_FOR_UOF]: !!isLoomEnabled,
      [STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS]: !!isMultiInstanceAppsEnabled,
    });
  }, [actions]);

  // this effect is needed to set the flag for toggling the unchanged line items + warning message
  useEffect(() => {
    setHasUnchangedLineItems(isAnyProductLeftUnchanged);
  }, [isAnyProductLeftUnchanged, setHasUnchangedLineItems]);

  // calling the API first for caching purpose so that on order-page this can quickly load
  useQuery<FetchAvailableInvoiceGroupsForOrderReviewQuery, FetchAvailableInvoiceGroupsForOrderReviewQueryVariables>(
    fetchAvailableInvoiceGroupsForOrderReview,
    {
      variables: {
        id: txaId,
      },
      fetchPolicy: 'cache-first',
      skip: !txaId,
    }
  );

  const { data: transactionAccountData, loading: transactionAccountDataLoading } = useQuery<
    FetchTransactionAccountForCreateBillingProfileQuery,
    FetchTransactionAccountForCreateBillingProfileQueryVariables
  >(fetchTransactionAccountForCreateBillingProfile, { variables: { id: txaId } });

  const [recurringestimate] = useLineItemsRecurringEstimateStore();
  const hasNewCloudSiteProducts = React.useMemo(() => {
    // checking whether products are already added to the new cloud site
    return productLineItems?.map((product) => product?.cloudSite || product?.orgName)?.some((item) => !item);
  }, [productLineItems]);

  const invoiceGroupDetails = selectedBillingProfileDetails || newInvoiceGroupDetails;

  /**
   * Get all the product Or App ids from the product line items
   * Using this to mark the bill estimates stale for line items after generating the PDF estimate
   */
  const productIds = React.useMemo(() => {
    return productLineItems.flatMap(
      (productLineItem) =>
        productLineItem?.products?.flatMap((item) => {
          if (item.type === ProductTypeEnum.CONTAINER) {
            return [...item.products, ...item.linkedEntitlements].map((productOrApp) => productOrApp.id);
          }
          if (item.type === ProductTypeEnum.STANDALONE) {
            return [...item.linkedEntitlements, item].map((productOrApp) => productOrApp.id);
          }
          return [];
        }) ?? []
    );
  }, [productLineItems]);

  const onAddCustomerSuccess = (responsePayload?: { id?: string; soldToName?: string }) => {
    const { id, soldToName } = responsePayload ?? {};
    sendAnalyticsEvent(AnalyticsTrackAction.SUCCESSFUL, AnalyticsTrackSubject.NEW_CUSTOMER, {
      txaId,
    });
    if (id && soldToName) {
      handleCustomerChangeOldFlow({ name: soldToName, ids: [id] }, true);
      closeAddCustomerModal();
    }
  };

  const onBillingProfileSubmit = (responsePayload?: {
    customerName?: string | null;
    customerId?: string;
    billingProfileInfo?: BillingProfileResponse;
  }) => {
    if (responsePayload?.customerName && responsePayload?.customerId) {
      actions.setSelectedCustomer({ name: responsePayload?.customerName, ids: [responsePayload?.customerId] });
    }
    if (responsePayload?.billingProfileInfo?.id && responsePayload?.billingProfileInfo?.name) {
      actions.setInvoiceGroup(responsePayload?.billingProfileInfo?.id);
      actions.setBillingProfiles([transformBillingProfileDetails(responsePayload?.billingProfileInfo)]);
      setNewInvoiceGroupDetails({
        name: responsePayload?.billingProfileInfo?.name,
        currency: responsePayload?.billingProfileInfo?.currency,
      });
    }
    closeCreateCustomerAndBillingProfileModal();
  };

  const onCloseCreateCustomerModal = () => {
    closeAddCustomerModal();
    openSearchCustomerModal();
  };

  const handleCustomerChangeOldFlow = (customer: CartCustomerSearchItem, isNewCustomer?: boolean) => {
    actions.setSelectedCustomer(customer);
    actions.resetStore();
    closeSearchCustomerModal();

    if ((isNonCommercialPricingEnabled || isMultiCurrencyChangesEnabled) && !isNewCustomer) {
      openDestinationBillingProfileModal();
    }
  };

  const handleEstimatePDFDownload = async (
    _invoiceGroup = invoiceGroupId,
    _isPartnerMarginIncluded = isPartnerMarginIncluded
  ) => {
    await downloadEstimatePDF({
      invoiceGroupId: _invoiceGroup,
      transactionAccountId: txaId,
      items: productLineItems,
      isPartnerMarginIncluded: _isPartnerMarginIncluded,
    });
  };

  const handleEstimateJSONDownload = async (
    _invoiceGroup = invoiceGroupId,
    _isPartnerMarginIncluded = isPartnerMarginIncluded
  ) => {
    await prepareAndSaveEstimateJSON({
      invoiceGroupId: _invoiceGroup,
      transactionAccountId: txaId,
      items: productLineItems,
      isPartnerMarginIncluded: _isPartnerMarginIncluded,
    });
  };

  const renderPartnerMarginModal = () => {
    if (!isNonCommercialPricingEnabled) {
      return (
        <EstimateModalWrapper
          isModalOpen={isModalOpen}
          transactionAccountId={txaId}
          customer={selectedCustomer as typeof initialCustomer}
          invoiceGroupId={invoiceGroupId}
          onClose={closeModalDialog}
          onSubmit={(invoiceGroupId, isPartnerMarginIncluded) => {
            actions.setInvoiceGroup(invoiceGroupId);
            actions.markBillEstimatesStale(productIds);
            typeOfExportSelected === 'PDF'
              ? handleEstimatePDFDownload(invoiceGroupId, isPartnerMarginIncluded)
              : handleEstimateJSONDownload(invoiceGroupId, isPartnerMarginIncluded);
            closeModalDialog();
          }}
          isPaypalEnabled={isSupportForPaypalEnabled}
        />
      );
    }

    const partnerDiscountsText = DISCOUNT_LABELS.PARTNER_DISCOUNT.toLowerCase() + 's';
    return (
      <ModalTransition>
        {isModalOpen && (
          <Modal onClose={closeModalDialog} testId="partner-margin-dialog-content">
            <ModalHeader>
              <ModalTitle>{`Include ${partnerDiscountsText}?`}</ModalTitle>
            </ModalHeader>
            <ModalBody>
              {`By default, the estimate PDF/JSON doesn't include ${partnerDiscountsText}. Select the checkbox to include them.`}
              <div className="partner-margin-checkbox">
                <Checkbox
                  label={`Include ${partnerDiscountsText}`}
                  isChecked={isPartnerMarginIncluded}
                  onChange={() => {
                    setIsPartnerMarginIncluded((prevValue) => !prevValue);
                  }}
                  testId="partner-margin-checkbox"
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={closeModalDialog}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  typeOfExportSelected === 'PDF' ? handleEstimatePDFDownload() : handleEstimateJSONDownload();
                  closeModalDialog();
                }}
                testId="partner-margin-dialog-continue-btn"
              >
                Continue
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>
    );
  };

  const ActionsButtonGroup = ({
    setActiveEntitlementsOpen,
    setSummaryDrawerOpen,
    hasNewCloudSiteProducts,
    totalItemsInCart,
    hasProductMetadataFetchFailed,
    isEstimatePDFDownloading,
    isEstimateJsonIsDownloading,
    isAnyProductLeftUnchanged,
    isCustomerSelected,
    isLatestBillEstimateFetched,
  }: {
    setActiveEntitlementsOpen: (flag: boolean) => void;
    setSummaryDrawerOpen: (flag: boolean) => void;
    isCustomerSelected: boolean;
    hasNewCloudSiteProducts: boolean;
    totalItemsInCart: number;
    hasProductMetadataFetchFailed: boolean | undefined;
    isEstimatePDFDownloading: boolean;
    isEstimateJsonIsDownloading: boolean;
    isAnyProductLeftUnchanged: boolean;
    isLatestBillEstimateFetched: boolean;
  }) => {
    return (
      <ButtonGroup>
        {isDesignIteration1Enabled ? (
          <AddToOrderActionsMenu
            onTransferClick={openTransferEntitlementsModal}
            openUpdateExistingEntitlementModal={openUpdateExistingEntitlementModal}
            onAddNewAppOrAddOnClick={() => {
              setShowProductListForAppSelection(true);
              openNewAppPurchaseModal();
            }}
            onAddNewProductsClick={() => {
              setSelectedCloudSiteForAddNewProducts(undefined);
              openAddNewProductsModal();
            }}
            isAddOnsPhase2Enabled={isAddOnsPhase2Enabled}
            isAppificationEnabled={isAppificationEnabled}
          />
        ) : (
          <>
            {!isActiveEntitlementsOpen && (
              <Button
                iconBefore={<WatchIcon label="add-active" />}
                appearance="default"
                onClick={() => setActiveEntitlementsOpen(true)}
              >
                Add active
              </Button>
            )}
            <AddNewProductsWrapper cloudSiteHost={''} isDisabled={hasNewCloudSiteProducts} />
          </>
        )}
        {isDesignIteration1Enabled ? (
          <EstimateAndOrderActionMenu
            isEstimatePDFDownloading={isEstimatePDFDownloading}
            isEstimateJsonIsDownloading={isEstimateJsonIsDownloading}
            onEstimationPDFClick={() => {
              setTypeOfExport('PDF');
              openModalDialog();
            }}
            onEstimationJSONClick={() => {
              setTypeOfExport('JSON');
              openModalDialog();
            }}
            onClearAllClick={() => {
              setIsClearAllLineItems(true);
            }}
            onRefreshClick={() => {
              if (hasProductEnrichmentFailed) return;
              actions.getBillEstimate();
            }}
            onClickOfReadyToOrder={() => {
              if (hasUnchangedLineItems) {
                setShowUnchangedLineItems(true);
                return;
              }
              setSummaryDrawerOpen(true);
              trackButtonClick(AnalyticsTrackSubject.REVIEW_ORDER, {
                source: AnalyticsEventSource.ORDER_SCREEN,
              });
            }}
            showRefreshOption={!isLatestBillEstimateFetched}
          />
        ) : (
          <>
            <OrderNowButtonWrapper
              isAnyProductLeftUnchanged={isAnyProductLeftUnchanged}
              isCustomerSelected={isCustomerSelected}
              isLatestBillEstimateFetched={isLatestBillEstimateFetched}
            >
              <Button
                iconBefore={<SendIcon label="order-now" />}
                appearance="primary"
                isDisabled={
                  !totalItemsInCart ||
                  hasProductMetadataFetchFailed ||
                  !isCustomerSelected ||
                  !isLatestBillEstimateFetched ||
                  // if selected billing profile is empty due to any reason, we are disabling the ready to order button
                  ((isMultiCurrencyChangesEnabled || isNonCommercialPricingEnabled) && !invoiceGroupDetails?.name)
                }
                onClick={() => {
                  if (hasUnchangedLineItems) {
                    setShowUnchangedLineItems(true);
                    return;
                  }
                  setSummaryDrawerOpen(true);
                  trackButtonClick(AnalyticsTrackSubject.REVIEW_ORDER, {
                    source: AnalyticsEventSource.ORDER_SCREEN,
                  });
                }}
              >
                Ready to order
              </Button>
            </OrderNowButtonWrapper>
            <MoreActionsMenu
              isEstimatePDFDownloading={isEstimatePDFDownloading}
              isEstimateJsonIsDownloading={isEstimateJsonIsDownloading}
              onEstimationPDFClick={() => {
                setTypeOfExport('PDF');
                openModalDialog();
              }}
              onEstimationJSONClick={() => {
                setTypeOfExport('JSON');
                openModalDialog();
              }}
              onClearAllClick={() => {
                setIsClearAllLineItems(true);
              }}
            />
          </>
        )}
      </ButtonGroup>
    );
  };

  const totalItemsInCart = Object.values(selectedProducts).length;

  const renderAddCustomerModal = () => {
    return (
      <AddCustomerModal
        onSuccess={onAddCustomerSuccess}
        isOpen={showAddCustomerModal}
        transactionAccountId={txaId}
        onClose={onCloseCreateCustomerModal}
      />
    );
  };

  const renderCreateCustomerAndBillingProfileModal = () => {
    return (
      <CreateCustomerAndBillingProfileModal
        isOpen={showCreateCustomerAndBillingProfileModal}
        transactionAccountId={txaId}
        onClose={closeCreateCustomerAndBillingProfileModal}
        onSubmit={onBillingProfileSubmit}
        transactionAccountData={transactionAccountData}
        transactionAccountDataLoading={transactionAccountDataLoading}
      />
    );
  };

  const renderSelectCustomerComponent = () => {
    return (
      <SearchCustomerModal
        isOpen={showSearchCustomerModal}
        onClose={closeSearchCustomerModal}
        onAddCustomerClick={() => {
          closeSearchCustomerModal();
          openAddCustomerModal();
          openCreateCustomerAndBillingProfileModal();
        }}
        onSelectCustomer={handleCustomerChangeOldFlow}
        isCreateCustomerEnabled={true}
        hideCustomerCloseModalButton={false}
        isFetchCustomerViaCCPEnabled={!isLoggedInUserCustomerAdvocate}
        isNewSearchDesignEnabled={isMultiCurrencyChangesEnabled}
      />
    );
  };

  const currency =
    isMultiCurrencyChangesEnabled || isNonCommercialPricingEnabled ? (invoiceGroupDetails?.currency as string) : 'USD';

  const priceEstimateDetails = [
    {
      label: isDesignIteration1Enabled
        ? sentenceCase(ESTIMATE_PRICING_ROW_LABELS.TOTAL_ITEM_SUBTOTAL)
        : ESTIMATE_PRICING_ROW_LABELS.TOTAL_ITEM_SUBTOTAL,
      value: recurringestimate?.totalListPrice,
    },
    {
      label: isDesignIteration1Enabled
        ? sentenceCase(ESTIMATE_PRICING_ROW_LABELS.TOTAL_OTHER_DISCOUNT)
        : ESTIMATE_PRICING_ROW_LABELS.TOTAL_OTHER_DISCOUNT,
      value: recurringestimate?.totalOtherDiscount,
    },
    {
      label: isDesignIteration1Enabled
        ? sentenceCase(ESTIMATE_PRICING_ROW_LABELS.TOTAL_PARTNER_DISCOUNT)
        : ESTIMATE_PRICING_ROW_LABELS.TOTAL_PARTNER_DISCOUNT,
      value: recurringestimate?.totalPartnerDiscount,
    },
    {
      label: isDesignIteration1Enabled
        ? sentenceCase(ESTIMATE_PRICING_ROW_LABELS.ADMIN_FEE)
        : ESTIMATE_PRICING_ROW_LABELS.ADMIN_FEE,
      value: recurringestimate?.totalAdminFee,
    },
    {
      label: isDesignIteration1Enabled
        ? sentenceCase(ESTIMATE_PRICING_ROW_LABELS.TOTAL_TAX)
        : ESTIMATE_PRICING_ROW_LABELS.TOTAL_TAX,
      value: recurringestimate?.totalTax,
    },
  ];

  const monthlyRecurringEstimateDetail = {
    label: isDesignIteration1Enabled
      ? sentenceCase(ESTIMATE_PRICING_ROW_LABELS.MONTHLY_RECURRING_ESTIMATE)
      : ESTIMATE_PRICING_ROW_LABELS.TOTAL_MONTHLY_RECURRING_ESTIMATE,
    value: recurringestimate?.totalAmountToPay,
  };

  const renderPriceEstimate = () => {
    return (
      <CollapsiblePricingEstimate
        title={monthlyRecurringEstimateDetail}
        data={priceEstimateDetails}
        currency={currency}
        showRefreshOption={!isLatestBillEstimateFetched && !isDesignIteration1Enabled}
        onRefreshClick={() => {
          if (hasProductEnrichmentFailed) return;

          actions.getBillEstimate();
        }}
        hasProductEnrichmentFailed={hasProductEnrichmentFailed}
        isProductEnrichmentPending={isProductEnrichmentPending}
        hideLastRefreshed={!!isDesignIteration1Enabled}
        showEmptyValuesInAmounts={!!isDesignIteration1Enabled}
        expandAndCollapseIconProps={isDesignIteration1Enabled ? { primaryColor: N30A, secondaryColor: N800 } : {}}
      />
    );
  };

  const getTransformedEstimate = (priceEstimateDetails: Array<{ label: string; value?: number }>) => {
    const transformedEstimateDetails = priceEstimateDetails.map((fieldDetail) => {
      return {
        label: fieldDetail.label,
        value: getFormattedCurrency(fieldDetail.value, currency, true),
      };
    });

    transformedEstimateDetails.push({
      label: monthlyRecurringEstimateDetail.label,
      value: getFormattedCurrency(monthlyRecurringEstimateDetail.value, currency, true),
    });

    return transformedEstimateDetails;
  };

  const renderNewPriceEstimateSection = () => {
    return (
      <StyledEstimateInfoSection>
        <InfoSection items={getTransformedEstimate(priceEstimateDetails)} columnsCount={6} />
      </StyledEstimateInfoSection>
    );
  };

  const breadcrumbsItems = React.useMemo(() => {
    const customerParams = new URLSearchParams({
      [CART_ROUTE_PARAMS.CUSTOMER]: queryParamCustomerName,
    });
    const orderParams = new URLSearchParams(location.search);
    const cartBasePath = getRouteUrl(routePaths.cart, {
      txaId: txaId as string,
    });
    return [
      {
        title: 'Home',
        href: routePaths.home,
      },
      {
        title: 'Select customer',
        href: routePaths.cart,
      },
      {
        title: selectedCustomerName as string,
        href: `${cartBasePath}?${customerParams.toString()}`,
      },
      {
        title: 'New order',
        href: `${cartBasePath}?${orderParams.toString()}`,
      },
    ];
  }, [selectedCustomerName, queryParamCustomerName]);

  const renderAppContent = () => {
    return (
      <AppContent
        className={classNames(
          { 'ppc-order-page-content': !isDesignIteration1Enabled },
          { 'blur-background': isDesignIteration1Enabled ? isRefreshEstimateLoading : isLoading }
        )}
      >
        <Prompt
          when={isDesignIteration1Enabled && Object.keys(selectedProducts).length > 0}
          message="Are you sure you want to navigate away, this will clear your cart?"
        />
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.ORDER_SCREEN,
            attributes: {},
          }}
        />
        {renderPartnerMarginModal()}
        {!isDesignIteration1Enabled && isLoading && (
          <div className="ppc-order-page-content-spinner-container">
            <Spinner size={'large'} />
          </div>
        )}
        {isRefreshEstimateLoading && (
          <div className="ppc-order-page-content-spinner-container">
            <Spinner size={'large'} />
          </div>
        )}
        {!isDesignIteration1Enabled && renderSelectCustomerComponent()}
        {!isDesignIteration1Enabled && !isMultiCurrencyChangesEnabled && renderAddCustomerModal()}
        {!isDesignIteration1Enabled && isMultiCurrencyChangesEnabled && renderCreateCustomerAndBillingProfileModal()}
        {!isDesignIteration1Enabled && isMultiCurrencyChangesEnabled && (
          <CreateBillingProfileModalForOrderFlow
            onSuccess={onBillingProfileSubmit}
            isOpen={showCreateBillingProfileModal}
            transactionAccountId={txaId}
            onClose={closeCreateBillingProfileModal}
            customer={selectedCustomer as typeof initialCustomer}
            transactionAccountData={transactionAccountData}
            transactionAccountLoading={transactionAccountDataLoading}
            isSoldToAddressArchivalEnabled={isSoldToAddressArchivalEnabled}
          />
        )}
        {!isDesignIteration1Enabled && (isNonCommercialPricingEnabled || isMultiCurrencyChangesEnabled) && (
          <DestinationBillingProfileWrapper
            isModalOpen={showDestinationBillingProfileModal}
            onClose={closeDestinationBillingProfileModal}
            transactionAccountId={txaId}
            customer={selectedCustomer as typeof initialCustomer}
            onSubmit={(billingProfileId) => {
              actions.setInvoiceGroup(billingProfileId);
              closeDestinationBillingProfileModal();
            }}
            isPaypalEnabled={isSupportForPaypalEnabled}
            openCreateBillingProfile={() => {
              closeDestinationBillingProfileModal();
              openCreateBillingProfileModal();
            }}
            showPrimaryContacts={isCustomerContactV3Enabled}
            isFetchBillingProfilesViaCCPEnabled={isFetchBillingProfilesViaCCPEnabled}
            isNewBPCreationFlowEnabled={isMultiCurrencyChangesEnabled || isNonCommercialPricingEnabled}
          />
        )}
        {isDesignIteration1Enabled && (
          <UpdateExistingEntitlementModal
            isOpen={showUpdateExistingEntitlementModal}
            onClose={() => {
              closeUpdateExistingEntitlementModal();
              actions.setSearchQuery('');
            }}
            onSuccess={(checkedIdsMap: Record<string, SelectType>) => {
              //selecting all the checked products to cart
              Object.entries(checkedIdsMap).forEach(([identifier, type]) => {
                actions.toggleSelectedItem(identifier, type, true, true);
                if (isTWCPhase2Enabled) {
                  // If the product is an org product, select all the instances of the product
                  products[identifier]?.isOrgProduct &&
                    orgProductInstancesMap[identifier]?.forEach((instance) => {
                      actions.toggleSelectedItem(instance.id, type, true, true);
                    });
                }
              });
              closeUpdateExistingEntitlementModal();
              actions.setSearchQuery('');
            }}
            customer={selectedCustomer}
            invoiceGroupId={selectedBillingProfileDetails?.id}
            isBillingProfileDetailsLoading={isBillingProfileDetailsLoading}
            billingProfileError={billingProfileError}
            refetchBillingProfileDetails={refetchBillingProfileDetails}
            fetchEntitlementListError={fetchEntitlementListError}
            isFetchEntitlementListLoading={isFetchEntitlementListLoading}
            refetchEntitlementsResponse={refetchEntitlementsResponse}
            externalProductsList={externalProductsList}
            isExternalProductListLoading={isExternalProductListLoading}
            isTWCPhase2Enabled={isTWCPhase2Enabled}
          />
        )}

        <NewAppPurchaseModal
          isOpen={showNewAppPurchaseModal}
          onClose={closeNewAppPurchaseModal}
          selectedProductDetails={selectedProductDetails}
          setSelectedProductDetails={setSelectedProductDetails}
          customer={selectedCustomer}
          invoiceGroupId={selectedBillingProfileDetails?.id}
          isBillingProfileDetailsLoading={isBillingProfileDetailsLoading}
          billingProfileError={billingProfileError}
          refetchBillingProfileDetails={refetchBillingProfileDetails}
          fetchEntitlementListError={fetchEntitlementListError}
          isFetchEntitlementListLoading={isFetchEntitlementListLoading}
          refetchEntitlementsResponse={refetchEntitlementsResponse}
          externalProductsList={externalProductsList}
          isExternalProductListLoading={isExternalProductListLoading}
          showProductListForAppSelection={showProductListForAppSelection}
        />
        {isDesignIteration1Enabled && (
          <TransferEntitlementsModal isOpen={transferEntitlementsModal} onClose={closeTransferEntitlementsModal} />
        )}
        <AddNewProductsModal
          isOpen={addNewProductsModal}
          onClose={() => {
            setSelectedCloudSiteForAddNewProducts(undefined);
            closeAddNewProductsModal();
          }}
          customer={selectedCustomer}
          {...(selectedCloudSiteForAddNewProducts && { ...selectedCloudSiteForAddNewProducts })}
          isBillingProfileDetailsLoading={isBillingProfileDetailsLoading}
          billingProfileError={billingProfileError}
          fetchEntitlementListError={fetchEntitlementListError}
          isFetchEntitlementListLoading={isFetchEntitlementListLoading}
        />

        <RightSideBar isActiveEntitlementsOpen={isActiveEntitlementsOpen}>
          <AddActiveEntitlementsSideBar
            customer={selectedCustomer}
            invoiceGroupId={invoiceGroupId}
            setActiveEntitlementsOpen={setActiveEntitlementsOpen}
            isSummaryDrawerOpen={isSummaryDrawerOpen}
            isNonCommercialPricingEnabled={isNonCommercialPricingEnabled}
          />
        </RightSideBar>
        <OrderMainContainer
          className={classNames('ppc-order-page-main-container', { 'full-width': !isActiveEntitlementsOpen })}
        >
          <OrderHeader>
            <div className="ppc-order-page-main-header">
              {!isDesignIteration1Enabled && (
                <CustomerName
                  onClick={openSearchCustomerModal}
                  customer={selectedCustomer ?? initialCustomer}
                  showEditCustomerButton={true}
                  invoiceGroupDetails={invoiceGroupDetails}
                  showCurrencyAndPriceEligibility={isMultiCurrencyChangesEnabled || isNonCommercialPricingEnabled}
                />
              )}
              {!isDesignIteration1Enabled && !!productLineItems?.length && (
                <ActionsButtonGroup
                  setActiveEntitlementsOpen={setActiveEntitlementsOpen}
                  setSummaryDrawerOpen={setSummaryDrawerOpen}
                  hasNewCloudSiteProducts={hasNewCloudSiteProducts}
                  totalItemsInCart={totalItemsInCart}
                  hasProductMetadataFetchFailed={hasProductMetadataFetchFailed}
                  isEstimatePDFDownloading={isEstimatePDFDownloading}
                  isEstimateJsonIsDownloading={isEstimateJsonIsDownloading}
                  isAnyProductLeftUnchanged={isAnyProductLeftUnchanged}
                  isCustomerSelected={!!selectedCustomer && selectedCustomer?.name !== '_'}
                  isLatestBillEstimateFetched={isLatestBillEstimateFetched}
                />
              )}
            </div>
          </OrderHeader>
          <LineItemsContainer>
            <div className="ppc-order-page-main-line-items">
              {!isDesignIteration1Enabled || (isDesignIteration1Enabled && totalItemsInCart > 0) ? (
                <CartFilterWrapper enableDesignIteration1Changes={isDesignIteration1Enabled} />
              ) : null}
              {isDesignIteration1Enabled && <div>{!!productLineItems?.length && renderNewPriceEstimateSection()}</div>}
              <ProductLineItemsWrapper
                emptyCart={
                  isDesignIteration1Enabled ? (
                    <EmptyCartV2
                      openUpdateExistingEntitlementModal={openUpdateExistingEntitlementModal}
                      onTransferClick={openTransferEntitlementsModal}
                      onAddNewAppOrAddOnClick={() => {
                        setShowProductListForAppSelection(true);
                        openNewAppPurchaseModal();
                      }}
                      onAddNewProductsClick={() => {
                        setSelectedCloudSiteForAddNewProducts(undefined);
                        openAddNewProductsModal();
                      }}
                      isLoading={isLoading || !selectedBillingProfileDetails} // show loader until destination billing profile details are available.
                      isAddOnsPhase2Enabled={isAddOnsPhase2Enabled}
                      isAppificationEnabled={isAppificationEnabled}
                    />
                  ) : (
                    <EmptyCart
                      isActiveEntitlementsOpen={isActiveEntitlementsOpen}
                      setActiveEntitlementsOpen={setActiveEntitlementsOpen}
                      hasNewCloudSiteProducts={hasNewCloudSiteProducts}
                    />
                  )
                }
                isClearAllLineItems={isClearAllLineItems}
                updateIsClearAllLineItems={setIsClearAllLineItems}
                hasUnchangedLineItems={hasUnchangedLineItems}
                showUnchangedLineItems={showUnchangedLineItems}
                onAddNewProductsClick={(cloudSiteId, cloudSiteHostName) => {
                  setSelectedCloudSiteForAddNewProducts({
                    cloudSiteId,
                    cloudSiteHostName,
                  });
                  openAddNewProductsModal();
                }}
                currency={
                  isMultiCurrencyChangesEnabled || isNonCommercialPricingEnabled
                    ? (invoiceGroupDetails?.currency as string)
                    : 'USD'
                }
                handleAddNewAppsClick={({
                  product,
                  cloudSiteId,
                  cloudSiteUrl,
                  onAddToCart,
                  sandboxCloudSiteParentProductIds,
                }: {
                  product: any;
                  cloudSiteId?: string;
                  cloudSiteUrl?: string;
                  onAddToCart: (OrderItems: CatalogueContextItems) => void;
                  sandboxCloudSiteParentProductIds: string[];
                }) => {
                  setShowProductListForAppSelection(false);
                  openNewAppPurchaseModal();
                  setSelectedProductDetails({
                    product,
                    cloudSiteId,
                    cloudSiteUrl,
                    onAddToCart,
                    sandboxCloudSiteParentProductIds,
                  });
                }}
                setHasErrorFetchingOfferings={setHasErrorFetchingOfferings}
              />
            </div>
          </LineItemsContainer>

          {!isDesignIteration1Enabled && (
            <div
              className={classNames('pricing-estimate', {
                'full-width': !isActiveEntitlementsOpen,
              })}
            >
              {!!productLineItems?.length && renderPriceEstimate()}
            </div>
          )}
        </OrderMainContainer>
        {isSummaryDrawerOpen && (
          <Drawer
            overrides={{
              Sidebar: {
                component: function BackButton() {
                  return (
                    <DrawerBackButton
                      setSummaryDrawerOpen={setSummaryDrawerOpen}
                      currentScreen={currentScreen}
                      resetStore={actions.resetStore}
                      setSelectedCustomer={actions.setSelectedCustomer}
                      setCustomer={actions.setSelectedCustomer}
                      open={openSearchCustomerModal}
                      isDesignIteration1Enabled={isDesignIteration1Enabled}
                    />
                  );
                },
              },
            }}
            width="full"
            onClose={() => setSummaryDrawerOpen(false)}
            isOpen
          >
            <OrderSummaryWrapper
              transactionAccountId={txaId}
              setSummaryDrawerOpen={setSummaryDrawerOpen}
              customer={selectedCustomer as typeof initialCustomer}
              setOrderDetails={setOrderDetails}
              onResetOrder={() => {
                actions.resetStore();
                actions.setSelectedCustomer({ name: '_', ids: [] });
                openSearchCustomerModal();
              }}
            />
          </Drawer>
        )}
      </AppContent>
    );
  };

  return isDesignIteration1Enabled ? (
    orderDetails?.hasOrderBeenPlaced ? (
      <PageBackgroundV2 heading="Confirmation">
        <OrderConfirmationContainer
          newSiteOrderId={orderDetails.newSiteOrderId}
          transactionAccountId={txaId}
          orderResponse={orderDetails.omsResponse}
          onTryAgainClick={() => {
            setSummaryDrawerOpen(false);
            actions.resetStore();
            actions.setSelectedCustomer(initialCustomer);
          }}
          isDesignIteration1Enabled={isDesignIteration1Enabled}
        />
      </PageBackgroundV2>
    ) : (
      <AppPage className="app-page-v2" id="order-page" allowedSidebars={allowedSidebars}>
        <StyledOrderPageBackground>
          {loading || soldToPartiesLoading ? (
            <OrderPageLoader />
          ) : (
            <PageBackgroundV2
              heading={selectedCustomer?.name}
              breadcrumbsProps={{ items: breadcrumbsItems }}
              actions={
                productLineItems?.length ? (
                  <ActionsButtonGroup
                    setActiveEntitlementsOpen={setActiveEntitlementsOpen}
                    setSummaryDrawerOpen={setSummaryDrawerOpen}
                    hasNewCloudSiteProducts={hasNewCloudSiteProducts}
                    totalItemsInCart={totalItemsInCart}
                    hasProductMetadataFetchFailed={hasProductMetadataFetchFailed}
                    isEstimatePDFDownloading={isEstimatePDFDownloading}
                    isEstimateJsonIsDownloading={isEstimateJsonIsDownloading}
                    isAnyProductLeftUnchanged={isAnyProductLeftUnchanged}
                    isCustomerSelected={!!selectedCustomer?.ids?.length}
                    isLatestBillEstimateFetched={isLatestBillEstimateFetched}
                  />
                ) : (
                  <></>
                )
              }
              subheading={
                <CustomerName
                  customer={selectedCustomer ?? initialCustomer}
                  showEditCustomerButton={false}
                  invoiceGroupDetails={selectedBillingProfileDetails}
                  showCurrencyAndPriceEligibility={true}
                  version={2}
                />
              }
            >
              {renderAppContent()}
            </PageBackgroundV2>
          )}
        </StyledOrderPageBackground>
      </AppPage>
    )
  ) : (
    <AppPage className="app-page-v2" id="order-page" allowedSidebars={allowedSidebars}>
      {renderAppContent()}
    </AppPage>
  );
};
